import { useMemo } from 'react';
import isEqual from 'lodash/isEqual';

const EMPTY_STRINGS = new Set(['', 'N/A']);

/**
 * Normalizes a value for comparison:
 *  - null/undefined and '' | 'N/A' (after trimming) all collapse to '' ("no value"),
 *    so "  VRT-120  " === "VRT-120" and empty/null/undefined/"N/A" are all treated as equal.
 *  - Empty arrays/objects also collapse to '', so an unset field and an empty
 *    selection (e.g. a multi-select that defaults to []) don't register as a change.
 *  - Everything else — including nested objects/arrays — passes through unchanged;
 *    `valuesDiffer` below compares those structurally rather than by string coercion.
 * @param {*} value
 * @returns {*} '' for "no value", otherwise the value unchanged
 */
function normalizeValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return EMPTY_STRINGS.has(trimmed) ? '' : trimmed;
  }
  if (Array.isArray(value) && value.length === 0) return '';
  if (typeof value === 'object' && Object.keys(value).length === 0) return '';
  return value;
}

/**
 * True if two values differ, after normalization. Objects/arrays (e.g. a
 * multi-select's list of {value,label} options, or a nested record) are
 * compared structurally via lodash `isEqual` — naively coercing them with
 * `String(...)` would collapse every object to the same "[object Object]"
 * text and silently hide real changes.
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
function valuesDiffer(a, b) {
  const normalizedA = normalizeValue(a);
  const normalizedB = normalizeValue(b);
  const eitherIsObject =
    (typeof normalizedA === 'object' && normalizedA !== null) ||
    (typeof normalizedB === 'object' && normalizedB !== null);
  if (eitherIsObject) {
    return !isEqual(normalizedA, normalizedB);
  }
  return String(normalizedA) !== String(normalizedB);
}

/**
 * Returns true if any of the given fields differ between initial and current (after normalization).
 * Reusable for any form: pass two objects and the list of keys to compare. Field values may be
 * primitives, arrays, or nested objects — all are compared correctly (see `valuesDiffer`).
 * @param {Object} initialValues - Initial values (e.g. when dialog opened)
 * @param {Object} currentValues - Current form values
 * @param {string[]} [fields] - Keys to compare. If omitted, compares all keys present in initialValues.
 * @returns {boolean} True if at least one field has changed
 */
export function hasFormChanges(initialValues, currentValues, fields) {
  const keys =
    Array.isArray(fields) && fields.length > 0
      ? fields
      : Object.keys(initialValues || {});
  return keys.some((key) =>
    valuesDiffer(initialValues?.[key], currentValues?.[key])
  );
}

/**
 * Hook that returns whether the current form values have changed from initial values.
 * Use to enable/disable Save only when something actually changed (e.g. type then revert = no change).
 * @param {Object} initialValues - Initial values (e.g. { deskId, timeZone, role } when dialog opened)
 * @param {Object} currentValues - Current form state
 * @param {string[]} [fields] - Keys to compare. If omitted, compares all keys in initialValues.
 * @returns {{ hasChanges: boolean }}
 */
export function useFormChanges(initialValues, currentValues, fields) {
  const fieldsKey = Array.isArray(fields) ? fields.join(',') : '';
  return useMemo(
    () => ({
      hasChanges: hasFormChanges(initialValues, currentValues, fields),
    }),
    [initialValues, currentValues, fieldsKey]
  );
}

export default useFormChanges;
